import { useState } from 'react';
import { MapView } from './MapView.tsx';
import { Icon } from './Icon.tsx';

/**
 * Постановка точки: картой, числами или «я сейчас здесь» (ТЗ 9.1).
 *
 * Четыре источника координат равноправны, и приложение не решает за
 * человека, какой из них верный. Но источник и точность записываются
 * всегда: без пометки точка из Mindat и точка из EXIF на карте выглядят
 * одинаково, хотя различаются на два порядка.
 */

const PRECISION_HINTS: { value: number; label: string }[] = [
  { value: 10, label: '10 м — стоял на точке' },
  { value: 100, label: '100 м — тот же карьер' },
  { value: 1000, label: '1 км — район' },
  { value: 10000, label: '10 км — «где-то там»' },
];

export function CoordPicker({
  latitude,
  longitude,
  precisionM,
  onChange,
  label = 'Координаты',
  radiusM,
}: {
  latitude: string;
  longitude: string;
  precisionM: string;
  onChange: (next: { latitude: string; longitude: string; precisionM: string }) => void;
  label?: string;
  /** Для домашней зоны: круг, внутри которого координаты игнорируются. */
  radiusM?: number;
}) {
  const [open, setOpen] = useState(false);
  const [locating, setLocating] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const lat = Number(latitude.replace(',', '.'));
  const lng = Number(longitude.replace(',', '.'));
  const valid = Number.isFinite(lat) && Number.isFinite(lng) && (lat !== 0 || lng !== 0);

  function set(nextLat: number, nextLng: number, precision?: number) {
    onChange({
      latitude: nextLat.toFixed(6),
      longitude: nextLng.toFixed(6),
      precisionM: precision === undefined ? precisionM : String(Math.round(precision)),
    });
  }

  function locate() {
    setError(null);
    setLocating(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        setLocating(false);
        set(position.coords.latitude, position.coords.longitude, position.coords.accuracy);
        setOpen(true);
      },
      () => {
        setLocating(false);
        setError('Не удалось получить координаты. Поставьте точку на карте или введите числами.');
      },
      { enableHighAccuracy: true, timeout: 15000 },
    );
  }

  return (
    <>
      <div className="label-key">{label}</div>
      <div className="coords-row">
        <input
          className="field-input"
          inputMode="decimal"
          placeholder="широта"
          value={latitude}
          onChange={(e) => onChange({ latitude: e.target.value, longitude, precisionM })}
        />
        <input
          className="field-input"
          inputMode="decimal"
          placeholder="долгота"
          value={longitude}
          onChange={(e) => onChange({ latitude, longitude: e.target.value, precisionM })}
        />
      </div>

      <div className="chips">
        <button type="button" className="chip chip--plain" onClick={() => setOpen((v) => !v)}>
          <Icon name="pin" className="icon icon-xs" /> {open ? 'свернуть карту' : 'на карте'}
        </button>
        <button type="button" className="chip chip--plain" disabled={locating} onClick={locate}>
          {locating ? 'определяем…' : 'я сейчас здесь'}
        </button>
        {valid && (
          <button
            type="button"
            className="chip chip--plain"
            onClick={() => onChange({ latitude: '', longitude: '', precisionM: '' })}
          >
            убрать точку
          </button>
        )}
      </div>

      {open && (
        <>
          <MapView
            className="map map-pick"
            points={[]}
            picked={valid ? { latitude: lat, longitude: lng, radius_m: radiusM } : null}
            onPick={(pickedLat, pickedLng) => set(pickedLat, pickedLng)}
          />
          <div className="hint">Нажмите на карту, чтобы поставить точку</div>
        </>
      )}

      {error && (
        <div className="notice notice--warn" style={{ marginTop: 'var(--space-2)' }}>
          <Icon name="alert" className="icon icon-sm" />
          <span>{error}</span>
        </div>
      )}

      {valid && radiusM === undefined && (
        <>
          <div className="label-key">Точность</div>
          <div className="chips">
            {PRECISION_HINTS.map((hint) => (
              <button
                key={hint.value}
                type="button"
                className={Number(precisionM) === hint.value ? 'chip' : 'chip chip--plain'}
                onClick={() =>
                  onChange({ latitude, longitude, precisionM: String(hint.value) })
                }
              >
                {hint.label}
              </button>
            ))}
          </div>
          <div className="hint">
            Без пометки о точности точка «где-то на Кольском» выглядит на карте так же
            уверенно, как снятая на самом обнажении
          </div>
        </>
      )}
    </>
  );
}
